import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  Carrier,
  ContactHandle,
  HealthCarrierType,
} from '@/components/carrier-directory/types';

import { baseQueryWithErrorHandling } from './baseQuery';

// Backend types (snake_case from API)
interface BackendContactHandle {
  id: string;
  value: string | null;
  handle_type: string;
  name: string | null;
  notes: string | null;
  sources: Array<{ url: string; title: string }> | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  last_used_at: string | null;
  usage_count: number;
  created_by_user_id: string | null;
  archived_at: string | null;
  archived_by_user_id: string | null;
}

interface BackendCarrier {
  id: string;
  name: string;
  normalized_name: string;
  aliases: string[];
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  health_carrier_type: string | null;
  handles: BackendContactHandle[];
  created_by_user_id: string | null;
  archived_at: string | null;
  archived_by_user_id: string | null;
}

// Division types
interface BackendDivision {
  id: string;
  carrier_id: string;
  division_name: string;
  is_default: boolean;
}

export interface CarrierDivision {
  id: string;
  carrierId: string;
  divisionName: string;
  isDefault: boolean;
}

interface BackendAddHandleResponse {
  handle: BackendContactHandle;
  created: boolean;
  match_reason: string | null;
}

interface AddHandleRequest {
  carrierId: string;
  handleType: string;
  value?: string | null;
  name?: string | null;
  description?: string | null;
  divisionId?: string | null;
  // Address fields for MAILING_ADDRESS type
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

interface UpdateHandleRequest {
  handleId: string;
  updates: Partial<ContactHandle>;
}

// Transform functions
const transformHandle = (backend: BackendContactHandle): ContactHandle => ({
  id: backend.id,
  value: backend.value,
  addressLine1: backend.address_line1,
  addressLine2: backend.address_line2,
  city: backend.city,
  state: backend.state,
  zip: backend.zip,
  country: backend.country,
  handleType: backend.handle_type as ContactHandle['handleType'],
  name: backend.name || '',
  description: backend.notes,
  source: backend.sources && backend.sources.length > 0 ? 'web_search' : null,
  lastUsedAt: backend.last_used_at,
  usageCount: backend.usage_count,
  createdByUserId: backend.created_by_user_id,
  archivedAt: backend.archived_at,
  archivedByUserId: backend.archived_by_user_id,
});

const transformCarrier = (backend: BackendCarrier): Carrier => ({
  id: backend.id,
  name: backend.name,
  normalizedName: backend.normalized_name,
  aliases: backend.aliases,
  addressLine1: backend.address_line1,
  addressLine2: backend.address_line2,
  city: backend.city,
  state: backend.state,
  zip: backend.zip,
  healthCarrierType: backend.health_carrier_type as HealthCarrierType | null,
  handles: backend.handles.map(transformHandle),
  createdByUserId: backend.created_by_user_id,
  archivedAt: backend.archived_at,
  archivedByUserId: backend.archived_by_user_id,
});

export const carriersApiService = createApi({
  reducerPath: 'carriersApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Carriers', 'Carrier', 'Handle'] as const,
  endpoints: (builder) => ({
    createCarrier: builder.mutation<
      Carrier,
      {
        name: string;
        addressLine1?: string | null;
        addressLine2?: string | null;
        city?: string | null;
        state?: string | null;
        zip?: string | null;
        healthCarrierType?: HealthCarrierType | null;
      }
    >({
      query: ({
        name,
        addressLine1,
        addressLine2,
        city,
        state,
        zip,
        healthCarrierType,
      }) => ({
        url: `api/carriers/`,
        method: 'POST',
        body: {
          name,
          address_line1: addressLine1,
          address_line2: addressLine2,
          city,
          state,
          zip,
          health_carrier_type: healthCarrierType,
        },
      }),
      transformResponse: (response: BackendCarrier) =>
        transformCarrier(response),
      invalidatesTags: () => [{ type: 'Carriers' }],
    }),
    updateCarrier: builder.mutation<
      Carrier,
      {
        carrierId: string;
        name?: string | null;
        aliases?: string[] | null;
        addressLine1?: string | null;
        addressLine2?: string | null;
        city?: string | null;
        state?: string | null;
        zip?: string | null;
        healthCarrierType?: string | null;
      }
    >({
      query: ({
        carrierId,
        name,
        aliases,
        addressLine1,
        addressLine2,
        city,
        state,
        zip,
        healthCarrierType,
      }) => ({
        url: `api/carriers/${carrierId}/`,
        method: 'PATCH',
        body: {
          name,
          aliases,
          address_line1: addressLine1,
          address_line2: addressLine2,
          city,
          state,
          zip,
          health_carrier_type: healthCarrierType,
        },
      }),
      transformResponse: (response: BackendCarrier) =>
        transformCarrier(response),
      invalidatesTags: (result, error, { carrierId }) => [
        { type: 'Carrier', id: carrierId },
        { type: 'Carriers' },
      ],
    }),
    // Divisions
    listDivisions: builder.query<CarrierDivision[], { carrierId: string }>({
      query: ({ carrierId }) => `api/carriers/${carrierId}/divisions/`,
      transformResponse: (response: BackendDivision[]) =>
        response.map((d) => ({
          id: d.id,
          carrierId: d.carrier_id,
          divisionName: d.division_name,
          isDefault: d.is_default,
        })),
      providesTags: (result, error, { carrierId }) => [
        { type: 'Carrier', id: carrierId },
      ],
    }),
    createDivision: builder.mutation<
      CarrierDivision,
      { carrierId: string; divisionName: string; isDefault?: boolean }
    >({
      query: ({ carrierId, divisionName, isDefault }) => ({
        url: `api/carriers/${carrierId}/divisions/`,
        method: 'POST',
        body: { division_name: divisionName, is_default: !!isDefault },
      }),
      transformResponse: (d: BackendDivision) => ({
        id: d.id,
        carrierId: d.carrier_id,
        divisionName: d.division_name,
        isDefault: d.is_default,
      }),
      invalidatesTags: (result, error, { carrierId }) => [
        { type: 'Carrier', id: carrierId },
      ],
    }),

    // Division handles
    listDivisionHandles: builder.query<
      ContactHandle[],
      { carrierId: string; divisionId: string }
    >({
      query: ({ carrierId, divisionId }) =>
        `api/carriers/${carrierId}/divisions/${divisionId}/handles/`,
      transformResponse: (response: BackendContactHandle[]) =>
        response.map(transformHandle),
      providesTags: (result, error, { carrierId, divisionId }) => [
        { type: 'Carrier', id: carrierId },
        { type: 'Handle', id: `${carrierId}-${divisionId}` },
      ],
    }),
    linkDivisionHandle: builder.mutation<
      void,
      { carrierId: string; divisionId: string; handleId: string }
    >({
      query: ({ carrierId, divisionId, handleId }) => ({
        url: `api/carriers/${carrierId}/divisions/${divisionId}/handles/${handleId}/link/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { carrierId, divisionId, handleId }) => [
        { type: 'Carrier', id: carrierId },
        { type: 'Handle', id: handleId },
        { type: 'Handle', id: `${carrierId}-${divisionId}` },
      ],
    }),
    unlinkDivisionHandle: builder.mutation<
      void,
      { carrierId: string; divisionId: string; handleId: string }
    >({
      query: ({ carrierId, divisionId, handleId }) => ({
        url: `api/carriers/${carrierId}/divisions/${divisionId}/handles/${handleId}/unlink/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { carrierId, divisionId, handleId }) => [
        { type: 'Carrier', id: carrierId },
        { type: 'Handle', id: handleId },
        { type: 'Handle', id: `${carrierId}-${divisionId}` },
      ],
    }),

    getCarrierDivision: builder.query<
      CarrierDivision,
      { divisionId: string }
    >({
      query: ({ divisionId }) => `api/carrier-divisions/${divisionId}/`,
      transformResponse: (d: BackendDivision) => ({
        id: d.id,
        carrierId: d.carrier_id,
        divisionName: d.division_name,
        isDefault: d.is_default,
      }),
      providesTags: (result) =>
        result ? [{ type: 'Carrier', id: result.carrierId }] : [],
    }),

    listCarriers: builder.query<Carrier[], void>({
      query: () => 'api/carriers/',
      transformResponse: (response: BackendCarrier[]) =>
        response.map(transformCarrier),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Carrier' as const, id })),
              { type: 'Carriers' as const },
            ]
          : [{ type: 'Carriers' as const }],
    }),

    getCarrier: builder.query<Carrier, string>({
      query: (id) => `api/carriers/${id}/`,
      transformResponse: (response: BackendCarrier) =>
        transformCarrier(response),
      providesTags: (result, error, id) => [{ type: 'Carrier', id }],
    }),

    addHandle: builder.mutation<ContactHandle, AddHandleRequest>({
      query: ({
        carrierId,
        handleType,
        value,
        name,
        description,
        divisionId,
        addressLine1,
        addressLine2,
        city,
        state,
        zip,
      }) => ({
        url: `api/carriers/${carrierId}/handles/`,
        method: 'POST',
        body: {
          handle_type: handleType,
          value,
          name,
          notes: description,
          division_id: divisionId,
          address_line1: addressLine1,
          address_line2: addressLine2,
          city,
          state,
          zip,
        },
      }),
      transformResponse: (response: BackendAddHandleResponse) =>
        transformHandle(response.handle),
      invalidatesTags: (result, error, { carrierId }) => [
        { type: 'Carrier', id: carrierId },
        { type: 'Carriers' },
      ],
    }),

    updateHandle: builder.mutation<ContactHandle, UpdateHandleRequest>({
      query: ({ handleId, updates }) => ({
        url: `api/contact-handles/${handleId}/`,
        method: 'PATCH',
        body: {
          handle_type: updates.handleType,
          value: updates.value,
          name: updates.name,
          notes: updates.description,
          address_line1: updates.addressLine1,
          address_line2: updates.addressLine2,
          city: updates.city,
          state: updates.state,
          zip: updates.zip,
          country: updates.country,
        },
      }),
      transformResponse: (response: BackendContactHandle) =>
        transformHandle(response),
      invalidatesTags: (result, error, { handleId }) => [
        { type: 'Handle', id: handleId },
        { type: 'Carriers' },
      ],
    }),
    archiveCarrier: builder.mutation<void, { carrierId: string }>({
      query: ({ carrierId }) => ({
        url: `api/carriers/${carrierId}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: 'Carriers' }],
    }),
    archiveHandle: builder.mutation<void, { handleId: string }>({
      query: ({ handleId }) => ({
        url: `api/contact-handles/${handleId}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: 'Carriers' }],
    }),
    unarchiveHandle: builder.mutation<void, { handleId: string }>({
      query: ({ handleId }) => ({
        url: `api/contact-handles/${handleId}/unarchive/`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: 'Carriers' }],
    }),
  }),
});

export const {
  useListCarriersQuery,
  useGetCarrierQuery,
  useGetCarrierDivisionQuery,
  useCreateCarrierMutation,
  useUpdateCarrierMutation,
  useListDivisionsQuery,
  useCreateDivisionMutation,
  useListDivisionHandlesQuery,
  useLinkDivisionHandleMutation,
  useUnlinkDivisionHandleMutation,
  useAddHandleMutation,
  useUpdateHandleMutation,
  useArchiveCarrierMutation,
  useArchiveHandleMutation,
  useUnarchiveHandleMutation,
} = carriersApiService;
